import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useState } from 'react';
import { adsLabels, trackAdsConversion, trackGoal } from '../analytics';
import { api } from '../api/client';
import type { Job, SiteConfig } from '../api/types';

interface CheckoutProps {
    job: Job;
    config: SiteConfig;
    onPaid: (job: Job) => void;
}

/**
 * PayPal checkout for a single mastered track.
 *
 * Unlike the legacy implementation the amount is never sent by the browser:
 * the order is created by the backend and the capture is verified there too.
 */
export function Checkout({ job, config, onPaid }: CheckoutProps) {
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    return (
        <div className="checkout">
            <div className="checkout__price">
                <span className="checkout__amount">
                    {config.currency === 'USD' ? '$' : ''}
                    {config.price}
                </span>
                <span className="checkout__unit">
                    {config.currency} · one full length master, yours forever
                </span>
            </div>

            <ul className="checkout__perks">
                <li>24 bit WAV master, full length</li>
                <li>No subscription, no account needed</li>
                <li>Instant download after payment</li>
            </ul>

            {error && <p className="alert alert--error">{error}</p>}

            <PayPalScriptProvider
                options={{
                    clientId: config.paypalClientId,
                    currency: config.currency,
                    intent: 'capture',
                }}
            >
                <PayPalButtons
                    style={{ layout: 'vertical', shape: 'pill', label: 'pay' }}
                    disabled={busy}
                    createOrder={async () => {
                        setError(null);
                        setBusy(true);
                        try {
                            const order = await api.createOrder(job.jobId);
                            return order.orderId;
                        } catch (err) {
                            setError(err instanceof Error ? err.message : 'The payment could not be started');
                            throw err;
                        } finally {
                            setBusy(false);
                        }
                    }}
                    onApprove={async (data) => {
                        setBusy(true);
                        try {
                            const paid = await api.captureOrder(job.jobId, data.orderID);
                            trackGoal('purchase', {
                                order_price: Number(config.price),
                                currency: config.currency,
                            });
                            // Google Ads needs the value and a stable
                            // transaction id, otherwise a reload of the
                            // success screen is counted as a second sale.
                            trackAdsConversion(adsLabels.purchase, {
                                value: Number(config.price),
                                currency: config.currency,
                                transaction_id: data.orderID,
                            });
                            onPaid(paid);
                        } catch (err) {
                            setError(
                                err instanceof Error
                                    ? err.message
                                    : 'The payment could not be verified, please contact support',
                            );
                        } finally {
                            setBusy(false);
                        }
                    }}
                    onError={() =>
                        setError('PayPal reported an error, please try again or use another payment method')
                    }
                />
            </PayPalScriptProvider>

            {config.paypalMode !== 'live' && (
                <p className="checkout__sandbox">PayPal sandbox mode — no real money is charged.</p>
            )}
        </div>
    );
}
